import Accordion from '@/components/shared/Accordion/accordion.component';
import Tabs from '@/components/shared/Tabs/tabs.component';
import Link from '@/components/WrappedLink';
import Typography from '@/components/shared/typography/typography.component';
import myImageLoader from '@/image.local.loader';
import {
    CarDetailsType,
    DisplayedCarInfoInterface,
    ImperfectionReportSectionGrouped,
} from '@/types';
import Image from 'next/image';
import {
    AlertCircleYellow,
    CheckCircleGreenGradient,
    InfoCircleBlue,
    AlertCircleInactive,
    CheckCircleInactive,
    NotesCircleInactive,
    CheckCircleGreen,
    ChevronDownArroy,
    chevronUpArrowBlack,
    CarFactoryPaint,
} from 'public/Images';
import { FC, Fragment, useEffect, useState } from 'react';
import AccordionGoodPartsContent from './accordionGoodPartsContent';
import NotesSectionContent from './notesSectionContent';
import ExteriorPartsContent from './exteriorPartsContent';
import BottomSheetNoPadding from '@/components/shared/bottom-sheet/bottom-sheet-no-padding.component';
import {
    AVAILABLE_LANGUAGES,
    NEXT_APP_MAINTENANCE_DISABLE_BOOKING,
} from '@/constants';
import SylndrButton from '@/components/shared/Button/button-styled.component';
import { useAnalytics } from '@/integrations';
import { TFunction } from 'i18next';

interface GrouppedImprefectionReportBottomSheetProps {
    open: boolean;
    setOpen: () => void;
    carDisplayName: string;
    carImage: string;
    imperfectionDataGroupped: ImperfectionReportSectionGrouped;
    initialActiveTab: number;
    highlights: {
        ar: string[];
        en: string[];
    } | null;
    lang: AVAILABLE_LANGUAGES;
    carDetails: CarDetailsType;
    carInfo: null | DisplayedCarInfoInterface;
    t: TFunction<'translation', undefined>;
    IS_RTL: boolean;
}

const GrouppedImprefectionReportBottomSheet: FC<
    GrouppedImprefectionReportBottomSheetProps
> = ({
    open,
    setOpen,
    carDisplayName,
    carImage,
    imperfectionDataGroupped,
    initialActiveTab,
    highlights,
    lang,
    carDetails,
    carInfo,
    IS_RTL,
    t,
}) => {
    const [currentOpenIndex, setCurrentOpenIndex] = useState<number | null>(
        null
    );
    const getCurrentOpenIndexFromAccordion = (currentIndex: number | null) => {
        setCurrentOpenIndex(currentIndex);
    };
    const { trackEvent } = useAnalytics();
    const [scrollY, setScrollyY] = useState<number | null>(null);
    useEffect(() => {
        const scrollPosition = window.scrollY;
        if (open) {
            setScrollyY(scrollPosition);
            // Save the current scroll position

            // Lock the scroll position
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollPosition}px`; // Maintain the scroll position by offsetting the top
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.overflow = 'hidden';
            document.body.style.width = '100vw'; // Prevent unwanted horizontal scrolling
        } else {
            // Re-enable scrolling by removing the styles
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.overflow = '';
            document.body.style.width = '';

            // Restore the scroll position
            window.scrollTo({ top: scrollY, behavior: 'instant' });
        }

        // Cleanup when component unmounts or the bottom sheet closes
        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.overflow = '';
            document.body.style.width = '';
            window.scrollTo(0, scrollY);
        };
    }, [open]);
    return (
        <BottomSheetNoPadding
            open={open}
            setOpen={setOpen}
            className="!h-[80vh] !p-0"
        >
            <div className="w-full min-h-full  bg-[#FFF] bg-gradient-to-b from-[#F3FFFA] via-[#F3FFFA00]/[4%]  flex flex-col relative">
                <div className="bg-gray-300 h-1 w-20 rounded-[100px] mx-auto mt-3" />
                <div className="flex gap-2  p-4 items-center w-full ">
                    <div className="w-[53px] h-10  ">
                        <img
                            className="rounded"
                            src={myImageLoader({
                                src: carImage,
                                width: 200,
                                height: 150,
                                quality: 80,
                            })}
                            alt="car image"
                        />
                    </div>
                    <div className="flex flex-col">
                        <Typography
                            variant="body2SemiBold"
                            className="!text-gray-800"
                        >
                            {t('Inspection Report')}
                        </Typography>
                        <Typography
                            variant="captionMedium"
                            className="!text-gray-600"
                        >
                            {carDisplayName}
                        </Typography>
                    </div>
                </div>
                {open && (
                    <Tabs
                        tabContainerClass="w-full overflow-x-scroll px-4"
                        initialActiveTab={initialActiveTab}
                        tabContentContainerClass="left-0  mt-4 absolute w-screen max-h-[calc(70vh-100px)] px-4 overflow-x-hidden"
                        tabsData={[
                            {
                                title: t('exteriorAndRepaintedTitle'),
                                content: (
                                    <>
                                        {!imperfectionDataGroupped.isRepainted && (
                                            <div className="flex flex-col items-center mb-6 py-4">
                                                <Typography
                                                    variant="body2SemiBold"
                                                    className="!text-gray-700"
                                                >
                                                    {t('factoryPaint')}
                                                </Typography>
                                                <img
                                                    src={myImageLoader({
                                                        src: CarFactoryPaint.src,
                                                        width: 245,
                                                        height: 117,
                                                        quality: 80,
                                                    })}
                                                    className="h-28 w-60 ar:scale-x-[-1]"
                                                    alt="factoy-paint"
                                                />
                                            </div>
                                        )}
                                        <ExteriorPartsContent
                                            section={
                                                imperfectionDataGroupped.exterior
                                            }
                                            IS_RTL={IS_RTL}
                                        />
                                    </>
                                ),
                                activeIcon: imperfectionDataGroupped.isRepainted
                                    ? AlertCircleYellow
                                    : CheckCircleGreenGradient,
                                inactiveIcon:
                                    imperfectionDataGroupped.isRepainted
                                        ? AlertCircleInactive
                                        : CheckCircleInactive,
                                activeBgColor:
                                    imperfectionDataGroupped.isRepainted
                                        ? 'bg-warning-25'
                                        : 'bg-primary-25',
                                activeBorderColor:
                                    imperfectionDataGroupped.isRepainted
                                        ? 'border-warning-600'
                                        : 'border-primary-600',
                                activeTextColor:
                                    imperfectionDataGroupped.isRepainted
                                        ? 'text-warning-600'
                                        : 'text-primary-600',
                            },
                            {
                                title: t('goodParts'),
                                content: (
                                    <>
                                        <Accordion
                                            getCurrentOpenIndexFromAccordion={
                                                getCurrentOpenIndexFromAccordion
                                            }
                                            items={[
                                                ...imperfectionDataGroupped.unFaulty.map(
                                                    (section, index) => {
                                                        return {
                                                            content: (
                                                                <div
                                                                    key={`${section.name_en} section-content`}
                                                                >
                                                                    <div className="w-full h-[1px] mb-4 bg-gray-200 " />
                                                                    <div className="flex flex-col gap-3">
                                                                        <AccordionGoodPartsContent
                                                                            arr={
                                                                                section.items
                                                                            }
                                                                            IS_RTL={
                                                                                IS_RTL
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ),
                                                            accordionTabLayout:
                                                                (
                                                                    <Fragment
                                                                        key={`${section.name_en} section-layout`}
                                                                    >
                                                                        <div className="en:mr-2.5 ar:ml-2.5 w-6 h-6 self-start">
                                                                            <img
                                                                                className="object-fill h-full w-full"
                                                                                src={
                                                                                    section.section_good_icon
                                                                                }
                                                                                alt="good section"
                                                                            />
                                                                        </div>
                                                                        <div className="flex flex-col gap-1 w-[calc(100%-90px)]">
                                                                            <Typography
                                                                                variant="body2SemiBold"
                                                                                className="!text-gray-700 en:hidden ar:mt-0.5"
                                                                            >
                                                                                {
                                                                                    section.name_ar
                                                                                }
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2SemiBold"
                                                                                className="!text-gray-700 ar:hidden"
                                                                            >
                                                                                {
                                                                                    section.name_en
                                                                                }
                                                                            </Typography>

                                                                            {section.subtitle_en && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    className="!text-gray-500 ar:hidden line-clamp-2"
                                                                                >
                                                                                    {
                                                                                        section.subtitle_en
                                                                                    }
                                                                                </Typography>
                                                                            )}
                                                                            {section.subtitle_ar && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    className="!text-gray-500 en:hidden line-clamp-2"
                                                                                >
                                                                                    {
                                                                                        section.subtitle_ar
                                                                                    }
                                                                                </Typography>
                                                                            )}
                                                                        </div>
                                                                        <div
                                                                            className={`flex gap-2 en:ml-auto ar:mr-auto ${
                                                                                section.subtitle_en
                                                                                    ? ' self-start'
                                                                                    : ' '
                                                                            }`}
                                                                        >
                                                                            <Image
                                                                                src={
                                                                                    CheckCircleGreen
                                                                                }
                                                                                alt="green-tick"
                                                                            />
                                                                            <div className="w-5 h-5">
                                                                                <Image
                                                                                    src={
                                                                                        currentOpenIndex ===
                                                                                        index
                                                                                            ? chevronUpArrowBlack
                                                                                            : ChevronDownArroy
                                                                                    }
                                                                                    alt="green-tick"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </Fragment>
                                                                ),
                                                        };
                                                    }
                                                ),
                                            ]}
                                        />
                                    </>
                                ),
                                activeIcon: CheckCircleGreenGradient,
                                inactiveIcon: CheckCircleInactive,
                                activeBgColor: 'bg-primary-25',
                                activeBorderColor: 'border-primary-600',
                                activeTextColor: 'text-primary-600',
                            },
                            {
                                title: t('notesParts'),
                                content: (
                                    <NotesSectionContent
                                        highlights={highlights}
                                        imperfectionDataGroupped={
                                            imperfectionDataGroupped
                                        }
                                        IS_RTL={IS_RTL}
                                        t={t}
                                    />
                                ),
                                activeIcon: InfoCircleBlue,
                                inactiveIcon: NotesCircleInactive,
                                activeBgColor: 'bg-blue-25',
                                activeBorderColor: 'border-blue-600',
                                activeTextColor: 'text-blue-600',
                            },
                        ]}
                        inactiveTextColor="text-gray-500"
                    />
                )}
            </div>
            <div className="py-3 px-4 !w-screen border-t-gray-200 border-t absolute bottom-0 bg-white">
                <Link
                    disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                    href={`/${lang}/car-details/${carInfo?.id}/checkout/see`}
                    target="_blank"
                    className="flex justify-center items-center gap-2 !w-full bg-primary-600 rounded-lg h-12 "
                >
                    <SylndrButton
                        disabled={NEXT_APP_MAINTENANCE_DISABLE_BOOKING}
                        onClick={() => {
                            trackEvent('Action: Book Visit', {
                                carName: carDetails?.sfVehicleName,
                                newlyAdded:
                                    carDetails.isNewlyAdded &&
                                    !(
                                        carDetails.isReserved ||
                                        carDetails.isPurchaseInProgress
                                    ),
                                status: carDetails.isPurchaseInProgress
                                    ? 'Being Sold'
                                    : carDetails.isReserved
                                    ? 'Reserved'
                                    : 'Not Reserved',
                            });
                        }}
                        variant="primary"
                    >
                        <Typography
                            variant="body2Medium"
                            className="!text-white ar:mt-1"
                        >
                            {t('See Car')}
                        </Typography>
                    </SylndrButton>
                </Link>
            </div>
        </BottomSheetNoPadding>
    );
};

export default GrouppedImprefectionReportBottomSheet;
